import type { ReactNode } from "react";

/**
 * A very small Markdown renderer, for one document.
 *
 * The guide page renders `docs/desktop-client-integration.md` from source
 * rather than a copy of it, so that a doc edit cannot leave the console showing
 * something the repository no longer says. That means SOMETHING has to turn
 * Markdown into elements, and the choice was between a dependency and this.
 *
 * This won, narrowly, for two reasons. Every Markdown library worth using
 * renders arbitrary HTML — which is right for a general renderer and wrong for
 * a page inside an admin console, where the safe thing is to have no HTML path
 * at all. And the input is one file in this repository whose syntax is known:
 * headings, paragraphs, fenced code, tables, lists, blockquotes, and inline
 * code / bold / links. Everything else is deliberately not supported rather
 * than half-supported.
 *
 * Nothing here builds HTML from text. Every output is a React element and every
 * piece of document text lands in a text node, so a stray `<script>` in the
 * Markdown renders as the characters `<script>`.
 */

type Block =
  | { kind: "heading"; level: 2 | 3 | 4; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "code"; language: string; lines: string[] }
  | { kind: "table"; header: string[]; align: Align[]; rows: string[][] }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "quote"; lines: string[] }
  | { kind: "rule" };

type Align = "left" | "right" | "center";

export function Markdown({ source }: { source: string }) {
  return <div className="esm-doc">{parse(source).map(renderBlock)}</div>;
}

/** The document's title line, so a page can put it in its own header. */
export function markdownTitle(source: string): string {
  const line = source.split("\n").find((l) => l.startsWith("# "));
  return line ? line.slice(2).trim() : "";
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

function parse(source: string): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    if (line.trim() === "") {
      i++;
      continue;
    }

    // A fence swallows everything to its closing fence, verbatim. This has to
    // come first: the doc's code blocks contain `#`, `|` and `-`, every one of
    // which would otherwise be read as structure.
    if (line.startsWith("```")) {
      const language = line.slice(3).trim();
      const body: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").startsWith("```")) {
        body.push(lines[i] ?? "");
        i++;
      }
      i++; // the closing fence
      blocks.push({ kind: "code", language, lines: body });
      continue;
    }

    if (/^---+\s*$/.test(line)) {
      blocks.push({ kind: "rule" });
      i++;
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const hashes = heading[1]?.length ?? 1;
      // The `#` title is rendered by the page header, not inside the body.
      if (hashes > 1) {
        const level = Math.min(hashes, 4) as 2 | 3 | 4;
        blocks.push({ kind: "heading", level, text: heading[2] ?? "" });
      }
      i++;
      continue;
    }

    // A table is a header row, a delimiter row, then body rows.
    if (line.trim().startsWith("|") && (lines[i + 1] ?? "").trim().startsWith("|")) {
      const delimiter = (lines[i + 1] ?? "").trim();
      if (/^\|[\s:|-]+\|$/.test(delimiter)) {
        const header = splitRow(line);
        const align = splitRow(delimiter).map(alignOf);
        const rows: string[][] = [];
        i += 2;
        while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
          rows.push(splitRow(lines[i] ?? ""));
          i++;
        }
        blocks.push({ kind: "table", header, align, rows });
        continue;
      }
    }

    if (/^>\s?/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i] ?? "")) {
        body.push((lines[i] ?? "").replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ kind: "quote", lines: body });
      continue;
    }

    const bullet = /^\s*([-*]|\d+\.)\s+/.exec(line);
    if (bullet) {
      const ordered = /\d/.test(bullet[1] ?? "");
      const items: string[] = [];
      while (i < lines.length) {
        const current = lines[i] ?? "";
        const marker = /^\s*([-*]|\d+\.)\s+(.*)$/.exec(current);
        if (marker) {
          items.push(marker[2] ?? "");
          i++;
          continue;
        }
        // An indented continuation belongs to the item above it.
        if (/^\s{2,}\S/.test(current) && items.length > 0) {
          items[items.length - 1] += " " + current.trim();
          i++;
          continue;
        }
        break;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length) {
      const current = lines[i] ?? "";
      if (
        current.trim() === "" ||
        current.startsWith("```") ||
        current.startsWith("#") ||
        current.startsWith(">") ||
        /^---+\s*$/.test(current) ||
        current.trim().startsWith("|") ||
        /^\s*([-*]|\d+\.)\s+/.test(current)
      ) {
        break;
      }
      paragraph.push(current.trim());
      i++;
    }
    blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
  }

  return blocks;
}

function splitRow(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function alignOf(cell: string): Align {
  if (cell.startsWith(":") && cell.endsWith(":")) return "center";
  if (cell.endsWith(":")) return "right";
  return "left";
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderBlock(block: Block, index: number): ReactNode {
  switch (block.kind) {
    case "heading": {
      const id = slug(block.text);
      const content = inline(block.text);
      if (block.level === 2) return <h2 key={index} id={id}>{content}</h2>;
      if (block.level === 3) return <h3 key={index} id={id}>{content}</h3>;
      return <h4 key={index} id={id}>{content}</h4>;
    }
    case "paragraph":
      return <p key={index}>{inline(block.text)}</p>;
    case "code":
      return (
        <pre key={index} className="esm-doc__code" data-language={block.language || undefined}>
          <code>{block.lines.join("\n")}</code>
        </pre>
      );
    case "rule":
      return <hr key={index} />;
    case "quote":
      return (
        <blockquote key={index} className="esm-doc__note">
          {parse(block.lines.join("\n")).map(renderBlock)}
        </blockquote>
      );
    case "list": {
      const items = block.items.map((item, n) => <li key={n}>{inline(item)}</li>);
      return block.ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
    }
    case "table":
      return (
        // Wrapped rather than shrunk: several of these tables are wider than a
        // narrow window, and a table that wraps its cells stops being readable
        // long before it stops fitting.
        <div key={index} className="esm-doc__table-scroll">
          <table className="esm-table">
            <thead>
              <tr>
                {block.header.map((cell, n) => (
                  <th key={n} style={{ textAlign: block.align[n] ?? "left" }}>
                    {inline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} style={{ textAlign: block.align[c] ?? "left" }}>
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

/**
 * Inline spans: `code`, **bold**, *italic*, and [links](…).
 *
 * Tokenised with one alternation and rebuilt as elements. Deliberately not a
 * nested grammar — `**bold with `code` inside**` renders as literal text rather
 * than doing something clever, which is a limitation of the source document's
 * style rather than of its meaning.
 */
const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g;

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;

  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const token = match[0];

    if (token.startsWith("`")) {
      out.push(<code key={key++}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("**")) {
      out.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("*")) {
      out.push(<em key={key++}>{token.slice(1, -1)}</em>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      const label = link?.[1] ?? token;
      const href = link?.[2] ?? "";
      // Only http(s) becomes a link. A relative path in this document points at
      // a file in the repository, which the browser cannot open and which must
      // not become a navigation inside the console.
      out.push(
        /^https?:\/\//.test(href) ? (
          <a key={key++} href={href} target="_blank" rel="noreferrer noopener">
            {label}
          </a>
        ) : (
          <code key={key++}>{label}</code>
        ),
      );
    }
    last = at + token.length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

/** The `##` headings, for the page's own contents list. */
export function outline(source: string): { id: string; text: string }[] {
  return parse(source)
    .filter((b): b is Extract<Block, { kind: "heading" }> => b.kind === "heading" && b.level === 2)
    .map((b) => ({ id: slug(b.text), text: b.text }));
}
